import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  duplicateElements,
  reassignElementsToLayer,
  updateElements,
} from '../../features/layout/layoutSlice';
import { selectElement } from '../../features/selection/selectionSlice';
import { openRightDrawerTab } from '../../features/ui/uiSlice';
import { formatMeasurementLabel, metersToEffectiveWorldUnits, shouldUseDxfCalibration } from '../../utils/measurement';
import { CLASS_LABELS } from '../../constants';
import { deleteSelectedLayoutElements } from '../../utils/deleteCanvasSelection';
import {
  IconDuplicate,
  IconLayers,
  IconLock,
  IconSliders,
  IconTrash,
  VisibilityIcon,
} from '../icons/ContextMenuIcons';
import { LayoutElement, MeasurementUnit } from '../../types';
import {
  buildConnectorShiftUpdates,
  getConnectorShiftState,
  isConnectorElement,
} from '../../utils/connectorRail';

export type ElementContextMenuState = {
  x: number;
  y: number;
  elementId: string;
};

interface ElementContextMenuProps {
  menu: ElementContextMenuState;
  containerWidth: number;
  containerHeight: number;
  onClose: () => void;
}

const MENU_W = 300;
const MENU_H = 380;

const formatElementSize = (element: LayoutElement, measurementUnit: MeasurementUnit): string | null => {
  const lengthM = Number(element.customProperties?.lengthMeters);
  const thicknessM = Number(element.customProperties?.thicknessMeters);
  const widthLabel =
    Number.isFinite(lengthM) && lengthM > 0
      ? formatMeasurementLabel(lengthM, measurementUnit)
      : null;
  const heightLabel =
    Number.isFinite(thicknessM) && thicknessM > 0
      ? formatMeasurementLabel(thicknessM, measurementUnit)
      : null;

  if (widthLabel && heightLabel) return `${widthLabel} × ${heightLabel}`;
  if (widthLabel) return widthLabel;
  if (heightLabel) return heightLabel;
  return null;
};

const ElementContextMenu: React.FC<ElementContextMenuProps> = ({
  menu,
  containerWidth,
  containerHeight,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const menuRef = useRef<HTMLDivElement>(null);
  const elements = useAppSelector((state) => state.layout.elements);
  const layers = useAppSelector((state) => state.layout.layers);
  const layerOrder = useAppSelector((state) => state.layout.layerOrder);
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const measurementUnit = useAppSelector((state) => state.ui.measurementUnit);
  const floorPlanScaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  const dxfUnitsPerMeter = useAppSelector((state) => state.floorplan.dxfUnitsPerMeter);
  const floorplanBounds = useAppSelector((state) => state.floorplan.bounds);
  const floorplanPolylineCount = useAppSelector((state) => state.floorplan.polylines.length);
  const hasFloorPlanCalibration = shouldUseDxfCalibration(
    Boolean(floorplanBounds),
    floorplanPolylineCount
  );
  const effectiveScaleFactor = hasFloorPlanCalibration ? floorPlanScaleFactor : undefined;
  const effectiveDxfUnitsPerMeter = hasFloorPlanCalibration ? dxfUnitsPerMeter : undefined;

  const targetIds =
    selectedIds.includes(menu.elementId) && selectedIds.length > 0
      ? selectedIds
      : [menu.elementId];

  const targetElements = targetIds
    .map((id) => elements[id])
    .filter((el): el is NonNullable<typeof el> => !!el);

  const primary = elements[menu.elementId];
  const allLocked = targetElements.length > 0 && targetElements.every((el) => el.metadata.locked);
  const allHidden = targetElements.length > 0 && targetElements.every((el) => !el.metadata.visible);
  const orderedLayers = layerOrder.map((id) => layers[id]).filter(Boolean);

  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (!primary || targetElements.length === 0) return null;

  const typeLabel = CLASS_LABELS[primary.type] ?? primary.type;
  const isMultiSelect = targetElements.length > 1;
  const title = isMultiSelect
    ? `${targetElements.length} items`
    : primary.metadata.name || typeLabel;
  const sizeLabel = !isMultiSelect ? formatElementSize(primary, measurementUnit) : null;
  const currentLayerId = targetElements[0]?.layerId ?? '';
  const selectedLayerValue = targetElements.every((el) => el.layerId === currentLayerId)
    ? currentLayerId
    : '__mixed__';

  const left =
    menu.x + MENU_W + 8 > containerWidth
      ? Math.max(8, menu.x - MENU_W)
      : Math.max(8, menu.x);
  const top = Math.min(Math.max(8, menu.y), Math.max(8, containerHeight - MENU_H));

  const handleDuplicate = () => {
    dispatch(duplicateElements(targetIds));
    onClose();
  };

  const handleDelete = () => {
    deleteSelectedLayoutElements(dispatch, targetIds, elements);
    onClose();
  };

  const handleToggleLock = () => {
    const nextLocked = !allLocked;
    dispatch(
      updateElements(
        targetElements.map((el) => ({
          id: el.id,
          updates: {
            metadata: {
              ...el.metadata,
              locked: nextLocked,
            },
          },
        }))
      )
    );
    onClose();
  };

  const handleToggleVisibility = () => {
    const nextVisible = allHidden;
    dispatch(
      updateElements(
        targetElements.map((el) => ({
          id: el.id,
          updates: {
            metadata: {
              ...el.metadata,
              visible: nextVisible,
            },
          },
        }))
      )
    );
    onClose();
  };

  const handleLayerChange = (layerId: string) => {
    if (!layerId || layerId === '__mixed__') return;
    dispatch(reassignElementsToLayer({ elementIds: targetIds, layerId }));
    onClose();
  };

  const handleProperties = () => {
    if (!selectedIds.includes(menu.elementId)) {
      dispatch(selectElement(menu.elementId));
    }
    dispatch(openRightDrawerTab('properties'));
    onClose();
  };

  const connectorTargets = targetElements.filter(isConnectorElement);
  const canShiftConnectors = connectorTargets.length > 0;
  const allConnectorsShifted =
    canShiftConnectors &&
    connectorTargets.every((el) => getConnectorShiftState(el) === 'shifted');

  const handleToggleConnectorShift = () => {
    const nextState = allConnectorsShifted ? 'home' : 'shifted';
    const metersToWorld = (meters: number) =>
      metersToEffectiveWorldUnits(meters, effectiveScaleFactor, effectiveDxfUnitsPerMeter);
    const allElements = Object.values(elements);
    const patches = connectorTargets
      .map((el) => buildConnectorShiftUpdates(el, nextState, metersToWorld, allElements))
      .filter((p): p is NonNullable<typeof p> => !!p);
    if (patches.length === 0) {
      onClose();
      return;
    }
    dispatch(updateElements(patches));
    onClose();
  };

  const itemClass =
    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-40';
  const dividerClass = 'my-1 border-t border-gray-200';

  return (
    <div
      ref={menuRef}
      className="absolute z-30 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl"
      style={{ left, top, width: MENU_W }}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white"
          style={{ backgroundColor: primary.metadata.color || '#6b7280' }}
          aria-hidden="true"
        >
          <IconLayers />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900">{title}</div>
          <div className="truncate text-xs text-gray-500">
            {isMultiSelect ? 'Multiple selection' : [typeLabel, sizeLabel].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      <div className={dividerClass} />

      <button type="button" className={itemClass} role="menuitem" onClick={handleDuplicate}>
        <IconDuplicate />
        <span className="flex-1">Duplicate</span>
      </button>
      <button
        type="button"
        className={`${itemClass} text-red-600 hover:bg-red-50`}
        role="menuitem"
        onClick={handleDelete}
      >
        <IconTrash />
        <span className="flex-1">Delete</span>
      </button>

      {canShiftConnectors && (
        <>
          <div className={dividerClass} />
          <button
            type="button"
            className={itemClass}
            role="menuitem"
            onClick={handleToggleConnectorShift}
          >
            <span className="w-5 text-center text-base" aria-hidden="true">
              ↔
            </span>
            <span className="flex-1">
              {allConnectorsShifted ? 'Solid on home leg' : 'Solid on far leg'}
            </span>
          </button>
        </>
      )}

      <div className={dividerClass} />

      <div className="px-3 py-2">
        <label className="mb-1.5 block text-xs font-medium text-gray-500">Move to layer</label>
        <select
          value={selectedLayerValue}
          onChange={(e) => handleLayerChange(e.target.value)}
          className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-800"
        >
          {selectedLayerValue === '__mixed__' && (
            <option value="__mixed__" disabled>
              Mixed layers
            </option>
          )}
          {orderedLayers.map((layer) => (
            <option key={layer.id} value={layer.id}>
              {layer.name}
            </option>
          ))}
        </select>
      </div>

      <div className={dividerClass} />

      <button type="button" className={itemClass} role="menuitem" onClick={handleToggleLock}>
        <IconLock />
        <span className="flex-1">{allLocked ? 'Unlock' : 'Lock'}</span>
      </button>
      <button type="button" className={itemClass} role="menuitem" onClick={handleToggleVisibility}>
        <VisibilityIcon visible={allHidden} />
        <span className="flex-1">{allHidden ? 'Show' : 'Hide'}</span>
      </button>

      <div className={dividerClass} />

      <button type="button" className={itemClass} role="menuitem" onClick={handleProperties}>
        <IconSliders />
        <span className="flex-1">Properties...</span>
      </button>
    </div>
  );
};

export default ElementContextMenu;
